import type {
  BorderWidth,
  Opacity,
  Percentage,
  Radius,
  Shadow,
  Size,
  Space,
  State,
} from '@opendoor/tokens-studio/dist/bricks-next/types/Novo';
import { getTokens, getTokenValue, isWeb } from '@tamagui/core';

import { getRGBA } from './convertColor';

export const getRadiusTokenValue = (radiusToken: Radius) => {
  const tokens = getTokens();
  return tokens.radius[radiusToken as keyof typeof tokens.radius].val;
};

export const getSizeTokenValue = (sizeToken: Size) => {
  const tokens = getTokens();
  return tokens.size[sizeToken as keyof typeof tokens.size].val;
};

export const getSpaceTokenValue = (spaceToken: Space | BorderWidth) => {
  const tokens = getTokens();
  return tokens.space[spaceToken as keyof typeof tokens.space].val;
};

// Tamagui doesn't currently use our opacity tokens for type-checking opacity props,
// so instead of `<View opacity={opacityToken} />`, we have to do
// `<View opacity={getOpacityTokenValue(opacityToken)} />`. Look into upgrading Tamagui
// and using the `acceptsTokens` config option to add this type-checking. See:
// https://tamagui.dev/docs/core/styled#custom-props-that-accepts-tokens-with-accepttokens
export const getOpacityTokenValue = (opacityToken: Opacity) => {
  const tokens = getTokens();
  return tokens.opacity[opacityToken as keyof typeof tokens.opacity].val;
};

export const getStateTokenValue = (stateToken: State) => {
  const tokens = getTokens();
  return tokens.state[stateToken as keyof typeof tokens.state].val;
};

export const getShadowProps = (shadowToken: Shadow) => {
  const tokens = getTokens();
  const shadowOpacity = tokens.shadowOpacity[shadowToken as keyof typeof tokens.shadowOpacity].val;
  // shadowOpacity doesn't seem to work on web, so use rgba to get the same effect
  const shadowColor = isWeb
    ? getRGBA(getTokenValue(shadowToken, 'color'), shadowOpacity)
    : shadowToken;
  return {
    shadowColor,
    shadowOpacity,
    shadowOffset: {
      width: tokens.shadowOffsetWidth[shadowToken as keyof typeof tokens.shadowOffsetWidth].val,
      height: tokens.shadowOffsetHeight[shadowToken as keyof typeof tokens.shadowOffsetHeight].val,
    },
    shadowRadius: tokens.shadowRadius[shadowToken as keyof typeof tokens.shadowRadius].val,
    elevationAndroid: tokens.elevation[shadowToken as keyof typeof tokens.elevation].val,
  };
};

export const getPercentageTokenValue = (percentageToken: Percentage) => {
  const tokens = getTokens();
  return tokens.percentages[percentageToken as keyof typeof tokens.percentages].val as `${number}%`;
};
